import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ClipboardList, ChevronRight, ChevronDown, MapPin, Users, AlertTriangle, Layers, Calendar, Download, User,
} from 'lucide-react';
import { toast } from 'sonner';
import { API, errorText } from '@/utils/api';
import OrderTakerReportDialog from '@/components/OrderTakerReportDialog';
import { MonthChips, RangeShortcuts, monthLabel, shiftMonth, thisMonth } from '@/components/MonthPicker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const FABRIC_COLORS = {
  Shirting: 'text-sky-700',
  Suiting: 'text-violet-700',
  Ethnic: 'text-amber-700',
  Other: 'text-slate-600',
  Unclear: 'text-rose-600',
};

const currentFYStart = () => {
  const now = new Date();
  return now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
};
const fyLabel = (sy) => `FY ${sy}-${String((sy + 1) % 100).padStart(2, '0')}`;

const fmtNum = (v) => Number(v || 0).toLocaleString('en-IN');

export default function OrderSummary() {
  const { user, selectedDivision } = useOutletContext();
  const canBackfill = user?.role === 'admin' || user?.role === 'accounts';

  const [periodType, setPeriodType] = useState('fy');
  const [fyYear, setFyYear] = useState(currentFYStart());
  const [monthValue, setMonthValue] = useState(new Date().toISOString().slice(0, 7));
  const [customFrom, setCustomFrom] = useState(`${currentFYStart()}-04`);
  const [customTo, setCustomTo] = useState(new Date().toISOString().slice(0, 7));
  const [customFromDate, setCustomFromDate] = useState(`${new Date().toISOString().slice(0, 7)}-01`);
  const [customToDate, setCustomToDate] = useState(new Date().toISOString().slice(0, 10));
  const [pickedMonths, setPickedMonths] = useState([shiftMonth(thisMonth(), -1), thisMonth()]);
  const [takerFilter, setTakerFilter] = useState('all');
  const [placeFilter, setPlaceFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [groupBy, setGroupBy] = useState('region'); // region | month | taker | place

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [takers, setTakers] = useState([]);
  const [places, setPlaces] = useState([]);
  const [expanded, setExpanded] = useState({});

  // Backfill
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [missing, setMissing] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);

  const fetchIdRef = useRef(0);

  useEffect(() => {
    // Independent fetches so one failure can't blank the other dropdown.
    axios.get(`${API}/order-takers`, { withCredentials: true })
      .then(t => setTakers([...(t.data?.order_takers || []), ...(t.data?.company_takers || [])]))
      .catch(e => console.error('Failed to load order takers', e));
    axios.get(`${API}/config/order-places`, { withCredentials: true })
      .then(p => setPlaces(p.data?.order_places || []))
      .catch(e => console.error('Failed to load order places', e));
  }, []);

  const periodValue = periodType === 'fy' ? String(fyYear)
    : periodType === 'custom' ? `${customFrom}:${customTo}`
    : periodType === 'months' ? [...pickedMonths].sort().join(',')
    : periodType === 'custom_dates' ? `${customFromDate}:${customToDate}`
    : monthValue;

  const fetchSummary = useCallback(async () => {
    const myId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const params = { period_type: periodType, period_value: periodValue };
      if (selectedDivision && selectedDivision !== 'all') params.division = selectedDivision;
      if (takerFilter !== 'all') params.employee_id = takerFilter;
      if (placeFilter !== 'all') params.place = placeFilter;
      if (regionFilter !== 'all') params.region = regionFilter;
      const res = await axios.get(`${API}/analytics/order-summary`, { params, withCredentials: true });
      if (myId === fetchIdRef.current) setData(res.data);
    } catch (e) {
      console.error('Failed to load order summary', e);
      if (myId === fetchIdRef.current) toast.error('Could not load order summary.');
    } finally {
      if (myId === fetchIdRef.current) setLoading(false);
    }
  }, [periodType, periodValue, selectedDivision, takerFilter, placeFilter, regionFilter]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const fabrics = data?.fabrics || ['Shirting', 'Suiting', 'Ethnic', 'Other', 'Unclear'];
  const overall = data?.overall || {};
  const regions = data?.regions || [];

  const fyYears = useMemo(() => {
    const cur = currentFYStart();
    return Array.from({ length: 6 }, (_, i) => cur - i);
  }, []);

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));
  const cell = (obj) => fmtNum(obj?.units);

  const canExport = user?.role === 'admin' || user?.role === 'accounts';
  const [exporting, setExporting] = useState(false);

  // Export dialog — duration, location, employee, region (each with an 'all'
  // option) and the breakdown detail (by month vs BY CUSTOMER, which names
  // every customer — the analysis view). Seeds from the page's active filters.
  const [exportOpen, setExportOpen] = useState(false);
  const [takerReportOpen, setTakerReportOpen] = useState(false);
  const [exType, setExType] = useState('fy');
  const [exFy, setExFy] = useState(currentFYStart());
  const [exMonth, setExMonth] = useState(new Date().toISOString().slice(0, 7));
  const [exFrom, setExFrom] = useState(`${currentFYStart()}-04`);
  const [exTo, setExTo] = useState(new Date().toISOString().slice(0, 7));
  const [exFromDate, setExFromDate] = useState(`${new Date().toISOString().slice(0, 7)}-01`);
  const [exToDate, setExToDate] = useState(new Date().toISOString().slice(0, 10));
  const [exPicked, setExPicked] = useState([]);
  const [exTaker, setExTaker] = useState('all');
  const [exPlace, setExPlace] = useState('all');
  const [exRegion, setExRegion] = useState('all');

  const openExportDialog = () => {
    // Seed the dialog from whatever the page is currently showing.
    setExType(periodType); setExFy(fyYear); setExMonth(monthValue);
    setExFrom(customFrom); setExTo(customTo);
    setExFromDate(customFromDate); setExToDate(customToDate); setExPicked(pickedMonths);
    setExTaker(takerFilter); setExPlace(placeFilter); setExRegion(regionFilter);
    setExportOpen(true);
  };

  const exPeriodValue = () =>
    exType === 'fy' ? String(exFy)
      : exType === 'custom' ? `${exFrom}:${exTo}`
      : exType === 'months' ? [...exPicked].sort().join(',')
      : exType === 'custom_dates' ? `${exFromDate}:${exToDate}`
      : exMonth;

  const downloadSummary = async (format) => {
    if (exType === 'months' && exPicked.length === 0) { toast.error('Pick at least one month'); return; }
    setExporting(true);
    try {
      const params = {
        period_type: exType, period_value: exPeriodValue(),
        view: 'region', breakdown: 'both', metric: 'units', format,
      };
      if (selectedDivision && selectedDivision !== 'all') params.division = selectedDivision;
      if (exTaker !== 'all') params.employee_id = exTaker;
      if (exPlace !== 'all') params.place = exPlace;
      if (exRegion !== 'all') params.region = exRegion;
      const res = await axios.get(`${API}/analytics/order-summary/export`, {
        params, withCredentials: true, responseType: 'blob', timeout: 180000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const scopeBits = [
        'monthwise_customerwise',
        selectedDivision && selectedDivision !== 'all' ? selectedDivision : 'AllDiv',
        exTaker !== 'all' ? (takers.find((t) => t.employee_id === exTaker)?.name || 'taker') : null,
        exPlace !== 'all' ? exPlace : null,
        exRegion !== 'all' ? exRegion : null,
        exPeriodValue(),
      ].filter(Boolean).join('_').replace(/[^A-Za-z0-9_-]+/g, '');
      link.setAttribute('download', `OrderSummary_${scopeBits}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Order summary ${format.toUpperCase()} downloaded`);
      setExportOpen(false);
    } catch (err) {
      let msg = err.message || 'Export failed';
      try {
        const blobText = await (err.response?.data?.text?.() || Promise.resolve(''));
        if (blobText) { try { msg = JSON.parse(blobText).detail || msg; } catch { msg = blobText.slice(0, 200); } }
      } catch {}
      toast.error(`Export failed: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  // Per-state report (duration picker, like the dispatches per-customer report).
  const [regionReport, setRegionReport] = useState(null); // region name | null
  const [rrType, setRrType] = useState('fy');
  const [rrFy, setRrFy] = useState(currentFYStart());
  const [rrMonth, setRrMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rrFrom, setRrFrom] = useState(`${currentFYStart()}-04`);
  const [rrTo, setRrTo] = useState(new Date().toISOString().slice(0, 7));
  const [rrFromDate, setRrFromDate] = useState(`${new Date().toISOString().slice(0, 7)}-01`);
  const [rrToDate, setRrToDate] = useState(new Date().toISOString().slice(0, 10));
  const [rrPicked, setRrPicked] = useState([]);
  const [rrTaker, setRrTaker] = useState('all');      // order-taker filter for the report
  const [rrPlace, setRrPlace] = useState('all');      // location/place filter for the report
  const [rrBusy, setRrBusy] = useState(false);
  const openRegionReport = (region) => {
    setRegionReport(region);
    setRrType(periodType); setRrFy(fyYear); setRrMonth(monthValue); // seed from current page period
    setRrFrom(customFrom); setRrTo(customTo);
    setRrFromDate(customFromDate); setRrToDate(customToDate); setRrPicked(pickedMonths);
    setRrTaker(takerFilter); setRrPlace(placeFilter); // inherit the page's active filters
  };
  const downloadRegionReport = async (format) => {
    if (!regionReport) return;
    if (rrType === 'months' && rrPicked.length === 0) { toast.error('Pick at least one month'); return; }
    setRrBusy(true);
    try {
      const params = {
        period_type: rrType,
        period_value: rrType === 'fy' ? String(rrFy)
          : rrType === 'custom' ? `${rrFrom}:${rrTo}`
          : rrType === 'months' ? [...rrPicked].sort().join(',')
          : rrType === 'custom_dates' ? `${rrFromDate}:${rrToDate}`
          : rrMonth,
        view: 'region', breakdown: 'both', region: regionReport, metric: 'units', format,
      };
      if (selectedDivision && selectedDivision !== 'all') params.division = selectedDivision;
      if (rrTaker !== 'all') params.employee_id = rrTaker;
      if (rrPlace !== 'all') params.place = rrPlace;
      const res = await axios.get(`${API}/analytics/order-summary/export`, {
        params, withCredentials: true, responseType: 'blob', timeout: 180000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const safe = regionReport.replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 30);
      link.setAttribute('download', `OrderSummary_${safe}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${regionReport} report downloaded`);
      setRegionReport(null);
    } catch (err) {
      toast.error(errorText(err) || 'Report download failed');
    } finally {
      setRrBusy(false);
    }
  };

  const unclearPct = overall.units > 0
    ? Math.round(((overall.fabrics?.Unclear?.units || 0) / overall.units) * 100)
    : 0;

  const openBackfill = async () => {
    setBackfillOpen(true);
    setMissingLoading(true);
    try {
      const params = {};
      if (selectedDivision && selectedDivision !== 'all') params.division = selectedDivision;
      const res = await axios.get(`${API}/orders/missing-metadata`, { params, withCredentials: true });
      // One entry per ORDER (order_no), not per product line.
      setMissing((res.data?.orders || []).map((o) => ({ ...o, _key: `${o.division}|${o.order_no}`, _taker: '', _place: '' })));
    } catch (e) {
      toast.error('Could not load orders needing backfill.');
    } finally {
      setMissingLoading(false);
    }
  };

  const saveBackfillRow = async (row) => {
    try {
      const takerObj = takers.find((t) => t.employee_id === row._taker);
      await axios.patch(`${API}/orders/by-order-metadata`, {
        division: row.division,
        order_no: row.order_no,
        order_taker_id: row._taker,
        order_taker_name: takerObj?.name || '',
        order_place: row._place,
      }, { withCredentials: true });
      toast.success(`Saved (${row.line_count} line${row.line_count === 1 ? '' : 's'})`);
      setMissing((prev) => prev.filter((m) => m._key !== row._key));
      fetchSummary();
    } catch (e) {
      toast.error(errorText(e) || 'Save failed');
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground mb-1 flex items-center gap-2">
            <ClipboardList size={26} /> Order Summary
          </h1>
          <p className="text-muted-foreground text-sm">
            Orders region-wise → customer-wise with fabric breakup
            {selectedDivision && selectedDivision !== 'all' ? ` · ${selectedDivision}` : ' · All divisions'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={periodType} onValueChange={setPeriodType}>
            <SelectTrigger className="h-9 w-28 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fy">Financial Year</SelectItem>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="custom">Month range</SelectItem>
              <SelectItem value="months">Pick months</SelectItem>
              <SelectItem value="custom_dates">Custom dates</SelectItem>
            </SelectContent>
          </Select>
          {periodType === 'fy' ? (
            <Select value={String(fyYear)} onValueChange={(v) => setFyYear(Number(v))}>
              <SelectTrigger className="h-9 w-32 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {fyYears.map((y) => <SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : periodType === 'month' ? (
            <Input type="month" value={monthValue} onChange={(e) => setMonthValue(e.target.value)} className="h-9 w-40 text-sm" />
          ) : periodType === 'custom' ? (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">From</span>
              <Input type="month" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 w-36 text-sm" />
              <span className="text-[11px] text-muted-foreground">to</span>
              <Input type="month" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 w-36 text-sm" />
              <RangeShortcuts onPick={(a, b) => { setCustomFrom(a); setCustomTo(b); }} />
            </div>
          ) : periodType === 'months' ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 text-sm max-w-[260px] truncate">
                  <Calendar size={14} className="mr-1" />
                  {pickedMonths.length ? [...pickedMonths].sort().map(monthLabel).join(', ') : 'Pick months'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[360px]" align="end">
                <p className="text-xs text-muted-foreground mb-1.5">Months ({pickedMonths.length} picked) — click to add or remove</p>
                <MonthChips value={pickedMonths} onChange={(v) => setPickedMonths(v.length ? v : pickedMonths)} />
              </PopoverContent>
            </Popover>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">From</span>
              <Input type="date" value={customFromDate} onChange={(e) => setCustomFromDate(e.target.value)} className="h-9 w-40 text-sm" />
              <span className="text-[11px] text-muted-foreground">to</span>
              <Input type="date" value={customToDate} onChange={(e) => setCustomToDate(e.target.value)} className="h-9 w-40 text-sm" />
            </div>
          )}
          <div className="h-6 w-px bg-border/60 mx-0.5" />
          <Button size="sm" variant="outline" className="h-9 text-indigo-700 border-indigo-300 hover:bg-indigo-50" onClick={() => setTakerReportOpen(true)}
            title="Who took the orders and how much has been dispatched to date — view on screen or download">
            <Users size={14} className="mr-1" /> Order-taker report…
          </Button>
          {canExport && (
            <>
              <Button size="sm" className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={openExportDialog}
                title="Download report — pick duration, location, employee, region and customer-wise detail">
                <Download size={14} className="mr-1" /> Download…
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Download scope — the division comes from the sidebar filter; everything
          else (duration, location, employee, region, customer-wise detail) is
          picked in the Download dialog. */}
      {canExport && (
        <p className="text-[11px] text-muted-foreground mb-3">
          Downloads cover:{' '}
          <span className={`font-medium ${(!selectedDivision || selectedDivision === 'all') ? 'text-amber-600' : 'text-foreground'}`}>
            {selectedDivision && selectedDivision !== 'all' ? selectedDivision : 'All divisions (whole company)'}
          </span>
          {(!selectedDivision || selectedDivision === 'all') && (
            <span className="text-amber-600"> — pick a division in the sidebar Division Filter to report on just that division.</span>
          )}
        </p>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-white rounded-xl border border-border/60 px-4 py-2.5 shadow-sm mb-5">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Filters</span>
        <Select value={takerFilter} onValueChange={setTakerFilter}>
          <SelectTrigger className="h-9 w-auto min-w-[150px] text-sm border-0 bg-gray-50"><SelectValue placeholder="Order taken by" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All order-takers</SelectItem>
            {takers.some(t => t.kind !== 'company') && (
              <SelectGroup>
                <SelectLabel className="text-[11px] font-bold text-indigo-600">Our team</SelectLabel>
                {takers.filter(t => t.kind !== 'company').map((t) => <SelectItem key={t.employee_id} value={t.employee_id}>{t.name}</SelectItem>)}
              </SelectGroup>
            )}
            {takers.some(t => t.kind === 'company') && (
              <SelectGroup>
                <SelectLabel className="text-[11px] font-bold text-amber-600">Company / principal employee</SelectLabel>
                {takers.filter(t => t.kind === 'company').map((t) => <SelectItem key={t.employee_id} value={t.employee_id}>{t.name}</SelectItem>)}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
        <Select value={placeFilter} onValueChange={setPlaceFilter}>
          <SelectTrigger className="h-9 w-auto min-w-[140px] text-sm border-0 bg-gray-50"><SelectValue placeholder="Place" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All places</SelectItem>
            {places.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="h-9 w-auto min-w-[130px] text-sm border-0 bg-gray-50"><SelectValue placeholder="Region" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All regions</SelectItem>
            {regions.map((r) => <SelectItem key={r.region} value={r.region}>{r.region}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="h-6 w-px bg-border/60 mx-1" />
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Group by</span>
        <div className="inline-flex rounded-lg border border-border/60 overflow-hidden">
          {[['region', 'Region'], ['month', 'Month'], ['taker', 'Order-taker'], ['place', 'Place']].map(([v, lbl]) => (
            <button key={v} onClick={() => setGroupBy(v)} className={`px-3 h-9 text-xs ${groupBy === v ? 'bg-indigo-600 text-white' : 'bg-white text-muted-foreground'}`}>{lbl}</button>
          ))}
        </div>
        {(takerFilter !== 'all' || placeFilter !== 'all' || regionFilter !== 'all') && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground hover:text-red-500"
            onClick={() => { setTakerFilter('all'); setPlaceFilter('all'); setRegionFilter('all'); }}>
            Clear
          </Button>
        )}
      </div>

      {/* Backfill banner */}
      {canBackfill && overall.missing_metadata_count > 0 && (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
          <p className="text-sm text-amber-800 flex items-center gap-2">
            <AlertTriangle size={15} />
            {overall.missing_metadata_count} order line{overall.missing_metadata_count === 1 ? '' : 's'} since {overall.metadata_since} are missing order-taker / place.
          </p>
          <Button size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100" onClick={openBackfill}>
            Backfill now
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KPI label="Order Units" value={fmtNum(overall.units)} />
            <KPI label="Customers" value={fmtNum(overall.customer_count)} icon={<Users size={14} />} />
            <KPI label="Regions" value={fmtNum(overall.region_count)} icon={<MapPin size={14} />} />
            <KPI label="Unclear fabric" value={`${unclearPct}%`} tone={unclearPct > 25 ? 'warn' : 'normal'} icon={<Layers size={14} />} />
          </div>

          {/* Fabric mix bar */}
          {overall.units > 0 && (
            <div className="mb-6 bg-white rounded-lg border border-border/60 shadow-sm p-4">
              <p className="text-xs font-medium text-muted-foreground mb-2">Fabric mix (by units)</p>
              <div className="flex h-3 rounded-full overflow-hidden">
                {fabrics.map((f) => {
                  const fv = overall.fabrics?.[f] || {};
                  const total = overall.units;
                  const part = fv.units;
                  const pct = total > 0 ? (part / total) * 100 : 0;
                  if (pct <= 0) return null;
                  const bg = { Shirting: 'bg-sky-500', Suiting: 'bg-violet-500', Ethnic: 'bg-amber-500', Other: 'bg-slate-400', Unclear: 'bg-rose-400' }[f];
                  return <div key={f} className={bg} style={{ width: `${pct}%` }} title={`${f}: ${cell(fv)} (${pct.toFixed(0)}%)`} />;
                })}
              </div>
              <div className="flex flex-wrap gap-3 mt-2">
                {fabrics.map((f) => {
                  const fv = overall.fabrics?.[f] || {};
                  if (!fv.units && !fv.value) return null;
                  return <span key={f} className={`text-[11px] ${FABRIC_COLORS[f]}`}>● {f}: <b>{cell(fv)}</b></span>;
                })}
              </div>
            </div>
          )}

          {/* ── View: Region / Order-taker / Place ── */}
          {groupBy === 'region' ? (
          regions.length === 0 ? (
            <Card><CardContent className="p-10 text-center text-muted-foreground">No orders for the selected period / filters.</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {regions.map((reg) => {
                const isOpen = expanded[reg.region] ?? true;
                return (
                  <div key={reg.region} className="border rounded-lg overflow-hidden bg-white">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-indigo-50">
                      <div className="flex items-center gap-2 cursor-pointer flex-1" onClick={() => toggle(reg.region)}>
                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <MapPin size={14} className="text-indigo-500" />
                        <span className="font-semibold text-sm">{reg.region}</span>
                        <span className="text-xs text-muted-foreground">({reg.customer_count} customers · {(reg.months || []).length} months)</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        {fabrics.map((f) => {
                          const fv = reg.fabrics?.[f];
                          if (!fv || (!fv.units && !fv.value)) return null;
                          return <span key={f} className={`hidden lg:inline ${FABRIC_COLORS[f]}`}>{f}: <b>{cell(fv)}</b></span>;
                        })}
                        <span className="font-semibold text-indigo-700">{fmtNum(reg.units)} u</span>
                        {canExport && (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] text-indigo-700 hover:bg-indigo-100"
                            onClick={(e) => { e.stopPropagation(); openRegionReport(reg.region); }}
                            title={`Download ${reg.region} order report — pick a duration`}>
                            <Download size={12} className="mr-1" /> Report…
                          </Button>
                        )}
                      </div>
                    </div>
                    {isOpen && (() => {
                      const rows = reg.months || [];
                      return (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-gray-50 text-[11px] uppercase tracking-wide text-muted-foreground">
                              <th className="text-left p-2.5">Month <span className="normal-case font-normal text-[10px] text-muted-foreground">(click to see customers)</span></th>
                              {fabrics.map((f) => <th key={f} className="text-right p-2.5">{f}</th>)}
                              <th className="text-right p-2.5 font-semibold">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.length === 0 ? (
                              <tr><td colSpan={fabrics.length + 2} className="p-3 text-center text-muted-foreground text-xs">No monthly data in this period.</td></tr>
                            ) : rows.map((m) => {
                              const mKey = `${reg.region}|${m.month}`;
                              const mOpen = !!expanded[mKey];
                              return (
                                <React.Fragment key={m.month}>
                                  <tr className="border-b last:border-b-0 hover:bg-indigo-50/40 cursor-pointer" onClick={() => toggle(mKey)} title="Click to see customers for this month">
                                    <td className="p-2.5 font-medium">
                                      <span className="inline-flex items-center gap-1">
                                        {mOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                        {m.month_label}
                                        <span className="text-[10px] text-muted-foreground">({(m.customers || []).length} cust)</span>
                                      </span>
                                    </td>
                                    {fabrics.map((f) => {
                                      const fv = m.fabrics?.[f];
                                      return <td key={f} className={`text-right p-2.5 ${fv && fv.units ? FABRIC_COLORS[f] : 'text-gray-300'}`}>{fv && fv.units ? cell(fv) : '—'}</td>;
                                    })}
                                    <td className="text-right p-2.5 font-semibold">{fmtNum(m.units)}</td>
                                  </tr>
                                  {mOpen && (m.customers || []).map((c) => (
                                    <tr key={`${m.month}|${c.customer_code}`} className="border-b bg-slate-50/50 text-xs">
                                      <td className="p-2 pl-8">
                                        {c.customer_name} <span className="text-muted-foreground">({c.customer_code})</span>
                                        {c.location && <span className="text-[10px] text-sky-700 font-medium ml-1">· {c.location}</span>}
                                        {c.source && c.source !== '—' && (
                                          <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground ml-1.5" title="Order place">
                                            <MapPin size={9} className="text-indigo-400" />{c.source}
                                          </span>
                                        )}
                                        {c.takers && c.takers !== '—' && (
                                          <span className="inline-flex items-center gap-0.5 text-[10px] text-indigo-600 ml-1.5" title="Order-taker">
                                            <User size={9} />{c.takers}
                                          </span>
                                        )}
                                      </td>
                                      {fabrics.map((f) => {
                                        const fv = c.fabrics?.[f];
                                        return <td key={f} className={`text-right p-2 ${fv && fv.units ? FABRIC_COLORS[f] : 'text-gray-300'}`}>{fv && fv.units ? cell(fv) : '—'}</td>;
                                      })}
                                      <td className="text-right p-2 font-semibold">{fmtNum(c.units)}</td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              );
                            })}
                            <tr className="bg-indigo-50/50 font-semibold">
                              <td className="p-2.5">Region total</td>
                              {fabrics.map((f) => <td key={f} className="text-right p-2.5">{reg.fabrics?.[f] ? cell(reg.fabrics[f]) : '—'}</td>)}
                              <td className="text-right p-2.5">{fmtNum(reg.units)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )
          ) : (
            <PivotView
              rows={groupBy === 'taker' ? (data?.by_taker || []) : groupBy === 'place' ? (data?.by_place || []) : (data?.by_month || [])}
              kind={groupBy}
              fabrics={fabrics}
              expanded={expanded}
              toggle={toggle}
              matureDays={data?.overall?.mature_days || 30}
            />
          )}
        </>
      )}

      {/* Main export dialog — duration, location, employee, region (each with
          an 'all' option) and month-wise vs CUSTOMER-wise detail, so a report
          can name every customer for analysis. */}
      <OrderTakerReportDialog
        open={takerReportOpen}
        onOpenChange={setTakerReportOpen}
        division={selectedDivision}
        takers={takers}
        places={places}
        regions={(data?.regions || []).map(r => r.region).filter(Boolean)}
        canDownload={canExport}
        initial={{ periodType, fyYear, monthValue, customFrom, customTo, customFromDate, customToDate, pickedMonths,
                   taker: takerFilter, place: placeFilter, region: regionFilter }}
      />

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download size={16} className="text-indigo-600" /> Download order summary
            </DialogTitle>
            <DialogDescription>
              Pick the duration, filters and detail level. Division follows the sidebar filter
              ({selectedDivision && selectedDivision !== 'all' ? selectedDivision : 'All divisions'}).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[11px] text-muted-foreground">
              One report with both breakdowns: month-wise, then customer-wise (every customer, with location and date-wise breakup).
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground w-16">Duration</span>
              <Select value={exType} onValueChange={setExType}>
                <SelectTrigger className="h-9 w-32 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fy">Financial Year</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="custom">Month range</SelectItem>
                  <SelectItem value="months">Pick months</SelectItem>
                  <SelectItem value="custom_dates">Custom dates</SelectItem>
                </SelectContent>
              </Select>
              {exType === 'fy' ? (
                <Select value={String(exFy)} onValueChange={(v) => setExFy(Number(v))}>
                  <SelectTrigger className="h-9 w-36 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {fyYears.map((y) => <SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : exType === 'month' ? (
                <Input type="month" value={exMonth} onChange={(e) => setExMonth(e.target.value)} className="h-9 w-44 text-sm" />
              ) : exType === 'custom' ? (
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground">From</span>
                    <Input type="month" value={exFrom} onChange={(e) => setExFrom(e.target.value)} className="h-9 w-36 text-sm" />
                    <span className="text-[11px] text-muted-foreground">to</span>
                    <Input type="month" value={exTo} onChange={(e) => setExTo(e.target.value)} className="h-9 w-36 text-sm" />
                  </div>
                  <RangeShortcuts className="mt-1.5" onPick={(a, b) => { setExFrom(a); setExTo(b); }} />
                </div>
              ) : exType === 'months' ? (
                <div className="w-full">
                  <p className="text-[11px] text-muted-foreground mb-1">Months ({exPicked.length} picked)</p>
                  <MonthChips value={exPicked} onChange={setExPicked} />
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground">From</span>
                  <Input type="date" value={exFromDate} onChange={(e) => setExFromDate(e.target.value)} className="h-9 w-40 text-sm" />
                  <span className="text-[11px] text-muted-foreground">to</span>
                  <Input type="date" value={exToDate} onChange={(e) => setExToDate(e.target.value)} className="h-9 w-40 text-sm" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground w-16">Filters</span>
              <Select value={exPlace} onValueChange={setExPlace}>
                <SelectTrigger className="h-9 w-[150px] text-sm"><SelectValue placeholder="Location" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {places.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={exTaker} onValueChange={setExTaker}>
                <SelectTrigger className="h-9 w-[165px] text-sm"><SelectValue placeholder="Order taken by" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All employees</SelectItem>
                  {takers.map((t) => <SelectItem key={t.employee_id} value={t.employee_id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={exRegion} onValueChange={setExRegion}>
                <SelectTrigger className="h-9 w-[140px] text-sm"><SelectValue placeholder="Region" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  {regions.map((r) => <SelectItem key={r.region} value={r.region}>{r.region}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded p-2">
              Report: <span className="font-medium">Month-wise + Customer-wise</span>
              {' · '}{selectedDivision && selectedDivision !== 'all' ? selectedDivision : 'All divisions'}
              {exPlace !== 'all' && <> · {exPlace}</>}
              {exTaker !== 'all' && <> · {takers.find((t) => t.employee_id === exTaker)?.name || 'Employee'}</>}
              {exRegion !== 'all' && <> · {exRegion}</>}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setExportOpen(false)} disabled={exporting}>Cancel</Button>
            <Button variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              disabled={exporting} onClick={() => downloadSummary('excel')}>
              <Download size={14} className="mr-1" /> {exporting ? 'Building…' : 'Excel'}
            </Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={exporting} onClick={() => downloadSummary('pdf')}>
              <Download size={14} className="mr-1" /> {exporting ? 'Building…' : 'PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-state report dialog — pick a duration, download that state's
          month-wise order report (PDF/Excel). */}
      <Dialog open={!!regionReport} onOpenChange={(o) => { if (!o) setRegionReport(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPin size={16} className="text-indigo-600" /> {regionReport} — order report</DialogTitle>
            <DialogDescription>Order breakup for {regionReport}. Pick a duration, then download.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[11px] text-muted-foreground">Month-wise and customer-wise in one report.</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={rrType} onValueChange={setRrType}>
                <SelectTrigger className="h-9 w-32 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fy">Financial Year</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="custom">Month range</SelectItem>
                  <SelectItem value="months">Pick months</SelectItem>
                  <SelectItem value="custom_dates">Custom dates</SelectItem>
                </SelectContent>
              </Select>
              {rrType === 'fy' ? (
                <Select value={String(rrFy)} onValueChange={(v) => setRrFy(Number(v))}>
                  <SelectTrigger className="h-9 w-36 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {fyYears.map((y) => <SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : rrType === 'month' ? (
                <Input type="month" value={rrMonth} onChange={(e) => setRrMonth(e.target.value)} className="h-9 w-44 text-sm" />
              ) : rrType === 'custom' ? (
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground">From</span>
                    <Input type="month" value={rrFrom} onChange={(e) => setRrFrom(e.target.value)} className="h-9 w-36 text-sm" />
                    <span className="text-[11px] text-muted-foreground">to</span>
                    <Input type="month" value={rrTo} onChange={(e) => setRrTo(e.target.value)} className="h-9 w-36 text-sm" />
                  </div>
                  <RangeShortcuts className="mt-1.5" onPick={(a, b) => { setRrFrom(a); setRrTo(b); }} />
                </div>
              ) : rrType === 'months' ? (
                <div className="w-full">
                  <p className="text-[11px] text-muted-foreground mb-1">Months ({rrPicked.length} picked)</p>
                  <MonthChips value={rrPicked} onChange={setRrPicked} />
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground">From</span>
                  <Input type="date" value={rrFromDate} onChange={(e) => setRrFromDate(e.target.value)} className="h-9 w-40 text-sm" />
                  <span className="text-[11px] text-muted-foreground">to</span>
                  <Input type="date" value={rrToDate} onChange={(e) => setRrToDate(e.target.value)} className="h-9 w-40 text-sm" />
                </div>
              )}
            </div>
            {/* Location + order-taker filters — the report honours these, so the
                download matches what you'd see with the same filters on-screen. */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground w-12">Filter</span>
              <Select value={rrTaker} onValueChange={setRrTaker}>
                <SelectTrigger className="h-9 w-[170px] text-sm"><SelectValue placeholder="Order taken by" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All order-takers</SelectItem>
                  {takers.map((t) => <SelectItem key={t.employee_id} value={t.employee_id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={rrPlace} onValueChange={setRrPlace}>
                <SelectTrigger className="h-9 w-[150px] text-sm"><SelectValue placeholder="Location" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {places.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded p-2">
              Scope: <span className="font-medium">{regionReport}</span>
              {' · '}{selectedDivision && selectedDivision !== 'all' ? selectedDivision : 'All divisions'}
              {rrTaker !== 'all' && <> · {takers.find((t) => t.employee_id === rrTaker)?.name || 'Order-taker'}</>}
              {rrPlace !== 'all' && <> · {rrPlace}</>}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRegionReport(null)} disabled={rrBusy}>Cancel</Button>
            <Button variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              disabled={rrBusy} onClick={() => downloadRegionReport('excel')}>
              <Download size={14} className="mr-1" /> {rrBusy ? 'Building…' : 'Excel'}
            </Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={rrBusy} onClick={() => downloadRegionReport('pdf')}>
              <Download size={14} className="mr-1" /> {rrBusy ? 'Building…' : 'PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backfill dialog */}
      <Dialog open={backfillOpen} onOpenChange={setBackfillOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle size={16} className="text-amber-600" /> Backfill order metadata</DialogTitle>
            <DialogDescription>
              Orders from {overall.metadata_since} onward that still need an order-taker and/or place. Set them here.
            </DialogDescription>
          </DialogHeader>
          {missingLoading ? (
            <div className="flex items-center justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
          ) : missing.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Nothing pending — all recent orders have taker + place.</p>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Order / Customer</th>
                    <th className="text-left p-2">Taken by</th>
                    <th className="text-left p-2">Place</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {missing.map((row) => (
                    <tr key={row._key} className="border-b last:border-b-0">
                      <td className="p-2 text-xs text-muted-foreground">{(row.order_date || '').slice(0, 10)}</td>
                      <td className="p-2 text-xs">
                        <div className="font-medium">{row.shop_name || row.customer_code}</div>
                        <div className="text-muted-foreground">{row.order_no} · {row.line_count} {row.line_count === 1 ? 'line' : 'lines'}</div>
                      </td>
                      <td className="p-2">
                        <Select value={row._taker} onValueChange={(v) => setMissing((prev) => prev.map((m) => m._key === row._key ? { ...m, _taker: v } : m))}>
                          <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {takers.some(t => t.kind !== 'company') && (
                              <SelectGroup>
                                <SelectLabel className="text-[11px] font-bold text-indigo-600">Our team</SelectLabel>
                                {takers.filter(t => t.kind !== 'company').map((t) => <SelectItem key={t.employee_id} value={t.employee_id}>{t.name}</SelectItem>)}
                              </SelectGroup>
                            )}
                            {takers.some(t => t.kind === 'company') && (
                              <SelectGroup>
                                <SelectLabel className="text-[11px] font-bold text-amber-600">Company / principal</SelectLabel>
                                {takers.filter(t => t.kind === 'company').map((t) => <SelectItem key={t.employee_id} value={t.employee_id}>{t.name}</SelectItem>)}
                              </SelectGroup>
                            )}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Select value={row._place} onValueChange={(v) => setMissing((prev) => prev.map((m) => m._key === row._key ? { ...m, _place: v } : m))}>
                          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{places.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Button size="sm" className="h-8 text-xs" disabled={!row._taker && !row._place} onClick={() => saveBackfillRow(row)}>Save</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBackfillOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Dispatch % colour: green 80%+, amber 50–80%, red below 50%.
const dispTone = (v) => (v == null ? 'text-slate-400' : v >= 80 ? 'text-emerald-700' : v >= 50 ? 'text-amber-700' : 'text-rose-700');

function PivotView({ rows, kind, fabrics, expanded, toggle, matureDays = 30 }) {
  const cell = (obj) => fmtNum(obj?.units);
  if (!rows.length) {
    return <Card><CardContent className="p-10 text-center text-muted-foreground">No orders for the selected period / filters.</CardContent></Card>;
  }
  const isTaker = kind === 'taker';
  const isMonth = kind === 'month';
  const PrimaryIcon = isTaker ? Users : isMonth ? Calendar : MapPin;
  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const name = isTaker ? row.taker_name : isMonth ? row.month_label : row.place;
        const key = `${kind}:${name}`;
        const isOpen = expanded[key] ?? false;
        const sub = isTaker ? row.places : isMonth ? row.regions : row.takers;
        const subLabel = isTaker ? 'By place' : isMonth ? 'By region' : 'By order-taker';
        return (
          <div key={key} className="border rounded-lg overflow-hidden bg-white">
            <div className="flex items-center justify-between px-4 py-2.5 bg-indigo-50 cursor-pointer hover:bg-indigo-100" onClick={() => toggle(key)}>
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <PrimaryIcon size={14} className="text-indigo-500" />
                <span className="font-semibold text-sm">{name}</span>
                <span className="text-xs text-muted-foreground">({row.customer_count} customers)</span>
              </div>
              <div className="flex items-center gap-4 text-xs">
                {fabrics.map((f) => {
                  const fv = row.fabrics?.[f];
                  if (!fv || (!fv.units && !fv.value)) return null;
                  return <span key={f} className={`hidden md:inline ${FABRIC_COLORS[f]}`}>{f}: <b>{cell(fv)}</b></span>;
                })}
                <span className="font-semibold text-indigo-700">{fmtNum(row.units)} u</span>
                {row.performance?.dispatch_pct != null && (
                  <span className={`font-semibold ${dispTone(row.performance.dispatch_pct)}`}
                    title={`Dispatched to date: ${fmtNum(row.performance.units_dispatched)} of ${fmtNum(row.performance.units_ordered)} units`}>
                    {row.performance.dispatch_pct}% dispatched
                  </span>
                )}
              </div>
            </div>
            {isOpen && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="text-left p-2.5">Fabric breakup</th>
                      {fabrics.map((f) => <th key={f} className="text-right p-2.5">{f}</th>)}
                      <th className="text-right p-2.5 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-2.5 text-muted-foreground">Fabric mix</td>
                      {fabrics.map((f) => {
                        const fv = row.fabrics?.[f];
                        return <td key={f} className={`text-right p-2.5 ${fv && (fv.units || fv.value) ? FABRIC_COLORS[f] : 'text-gray-300'}`}>{fv && (fv.units || fv.value) ? cell(fv) : '—'}</td>;
                      })}
                      <td className="text-right p-2.5 font-semibold">{fmtNum(row.units)}</td>
                    </tr>
                  </tbody>
                </table>
                {row.performance && (
                  <div className="px-4 py-2 border-t bg-white flex flex-wrap gap-x-5 gap-y-1 text-xs">
                    <span>Dispatched to date: <b>{fmtNum(row.performance.units_dispatched)}</b> of {fmtNum(row.performance.units_ordered)} units (<b className={dispTone(row.performance.dispatch_pct)}>{row.performance.dispatch_pct ?? '—'}%</b>)</span>
                    <span>Orders older than {matureDays} days: <b className={dispTone(row.performance.matured_dispatch_pct)}>{row.performance.matured_dispatch_pct ?? '—'}%</b> dispatched</span>
                    <span>Matured lines never dispatched: <b>{fmtNum(row.performance.matured_never_dispatched_lines)}</b> ({row.performance.matured_never_dispatched_pct ?? '—'}%)</span>
                  </div>
                )}
                {isTaker && (row.customers || []).length > 0 && (
                  <div className="border-t overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-[10px] uppercase tracking-wide text-muted-foreground">
                          <th className="text-left p-2">Customer</th>
                          <th className="text-left p-2">Location</th>
                          <th className="text-right p-2">Orders</th>
                          <th className="text-right p-2">Units</th>
                          <th className="text-right p-2">Dispatched</th>
                          <th className="text-right p-2">Dispatch %</th>
                          <th className="text-right p-2">&gt; {matureDays}d</th>
                        </tr>
                      </thead>
                      <tbody>
                        {row.customers.map((c) => (
                          <tr key={c.customer_code} className="border-t">
                            <td className="p-2">{c.customer_name} <span className="text-muted-foreground">({c.customer_code})</span></td>
                            <td className="p-2 text-sky-700">{c.location || '—'}</td>
                            <td className="p-2 text-right tabular-nums">{fmtNum(c.performance?.orders)}</td>
                            <td className="p-2 text-right tabular-nums">{fmtNum(c.performance?.units_ordered)}</td>
                            <td className="p-2 text-right tabular-nums">{fmtNum(c.performance?.units_dispatched)}</td>
                            <td className={`p-2 text-right tabular-nums font-semibold ${dispTone(c.performance?.dispatch_pct)}`}>{c.performance?.dispatch_pct ?? '—'}{c.performance?.dispatch_pct != null ? '%' : ''}</td>
                            <td className={`p-2 text-right tabular-nums ${dispTone(c.performance?.matured_dispatch_pct)}`}>{c.performance?.matured_dispatch_pct ?? '—'}{c.performance?.matured_dispatch_pct != null ? '%' : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Secondary cross-tab: taker→channel or place→rep */}
                <div className="px-4 py-2 bg-slate-50/60 border-t">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{subLabel}</p>
                  <div className="flex flex-wrap gap-2">
                    {(sub || []).map((s) => (
                      <span key={s.name} className="inline-flex items-center gap-1 text-xs bg-white border border-border/60 rounded-full px-2.5 py-1">
                        {s.name} <b className="text-indigo-700">{fmtNum(s.units)}</b>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function KPI({ label, value, icon, tone }) {
  return (
    <Card className={`border-border/60 shadow-sm ${tone === 'warn' ? 'bg-rose-50/40' : ''}`}>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">{icon}{label}</p>
        <p className={`text-2xl font-bold ${tone === 'warn' ? 'text-rose-600' : 'text-foreground'}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
